// DCA (Dollar Cost Averaging) Types

export interface DCAStrategy {
  id: string;
  name: string;
  userId: string;
  status: 'active' | 'paused' | 'completed' | 'cancelled';

  // Token configuration
  sourceToken: TokenInfo;
  targetToken: TokenInfo;

  // Investment parameters
  investmentAmount: string; // Amount per purchase
  totalBudget: string; // Total budget for the strategy
  usedBudget: string; // Amount already invested

  // Schedule
  frequency: DCAFrequency;
  nextExecutionTime: number; // Unix timestamp
  startDate: number;
  endDate?: number; // Optional end date

  // Smart features
  smartTiming: boolean; // AI-powered timing optimization
  priceThreshold?: PriceThreshold; // Only buy below/above certain price
  dipBuying: boolean; // Increase purchase on dips
  dipPercentage?: number; // % dip to trigger extra buy

  // Execution history
  executionCount: number;
  totalTokensAcquired: string;
  averagePurchasePrice: string;

  // Performance
  currentValue: string;
  profitLoss: string;
  profitLossPercentage: number;

  createdAt: number;
  updatedAt: number;
}

export interface TokenInfo {
  address: string;
  symbol: string;
  name: string;
  decimals: number;
  logoURI?: string;
  price?: number;
}

export type DCAFrequency =
  | 'hourly'
  | 'every_4_hours'
  | 'every_12_hours'
  | 'daily'
  | 'every_3_days'
  | 'weekly'
  | 'bi_weekly'
  | 'monthly';

export interface PriceThreshold {
  type: 'below' | 'above';
  price: string;
  enabled: boolean;
}

export interface DCAExecution {
  id: string;
  strategyId: string;
  timestamp: number;

  // Transaction details
  txHash: string;
  sourceAmount: string;
  targetAmount: string;
  executionPrice: string;

  // Fees
  gasFee: string;
  platformFee: string;

  // Status
  status: 'pending' | 'confirmed' | 'failed';
  errorMessage?: string;

  // Smart execution info
  wasSmartTimed: boolean;
  wasDipBuy: boolean;
  priceAtScheduledTime?: string;
  savedAmount?: string; // How much was saved by smart timing
}

export interface DCAStats {
  totalStrategies: number;
  activeStrategies: number;
  totalInvested: string;
  totalValue: string;
  overallPnL: string;
  overallPnLPercentage: number;
  totalExecutions: number;
  averageSavings: string; // Average saved through smart timing
}

export interface DCACreateParams {
  name: string;
  sourceToken: string; // Token address
  targetToken: string; // Token address
  investmentAmount: string;
  totalBudget: string;
  frequency: DCAFrequency;
  smartTiming: boolean;
  dipBuying: boolean;
  dipPercentage?: number;
  priceThreshold?: PriceThreshold;
  startDate?: number;
  endDate?: number;
}

export interface SmartTimingRecommendation {
  recommendedTime: number;
  currentPrice: string;
  expectedPrice: string;
  confidence: number; // 0-100
  reasoning: string;
  historicalAccuracy: number;
}

// Frequency display helpers
export const FREQUENCY_LABELS: Record<DCAFrequency, string> = {
  hourly: 'Cada hora',
  every_4_hours: 'Cada 4 horas',
  every_12_hours: 'Cada 12 horas',
  daily: 'Diario',
  every_3_days: 'Cada 3 dias',
  weekly: 'Semanal',
  bi_weekly: 'Quincenal',
  monthly: 'Mensual',
};

export const FREQUENCY_MS: Record<DCAFrequency, number> = {
  hourly: 60 * 60 * 1000,
  every_4_hours: 4 * 60 * 60 * 1000,
  every_12_hours: 12 * 60 * 60 * 1000,
  daily: 24 * 60 * 60 * 1000,
  every_3_days: 3 * 24 * 60 * 60 * 1000,
  weekly: 7 * 24 * 60 * 60 * 1000,
  bi_weekly: 14 * 24 * 60 * 60 * 1000,
  monthly: 30 * 24 * 60 * 60 * 1000,
};
